import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";

import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	Pagination,
	PaginationHeader,
	// ListFilterOptions
	PageHeaderListActions,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

// import { sortByPropertyDate } from 'utils'
import * as fromHealthSafetyForm from "features/health-safety-forms";
import StaffTrainingTopicCard from "../cards/staff-training-topic-card";
import {
	EditStaffTrainingTopicForm,
	PageHeaderTabs,
} from "features/health-safety-forms";
import { getPageResults } from "utils";

const useStyles = makeStyles((theme) => ({
	root: {},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const StaffTrainingTopicList = ({
	role,
	searchFilter,
	staffTrainingTopics,
	onDelete,
	onSearchChanged,
	onGetStaffTrainingTopicList,
}) => {
	const classes = useStyles();
	const [viewId, setViewId] = useState(null);
	const [editId, setEditId] = useState(null);
	const [showFilters, setShowFilters] = useState(false);
	const [localSearchFilter, setLocalSearchFilter] = useState(searchFilter || "");
	const isInitialMount = useRef(true);

	const staffTrainingTopicList = getPageResults(staffTrainingTopics);

	const empty = staffTrainingTopicList && staffTrainingTopicList.length <= 0;

	const onView = (id) => () => setViewId(id);

	const onEdit = (id) => () => setEditId(id);

	const onCancelEdit = () => {
		setEditId(null);
		setViewId(null);
	};

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			onSearchChanged(localSearchFilter);
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [localSearchFilter, onSearchChanged]);

	return (
		<Page className={classes.root} title="Staff Training Topic Library">
			<ScrollToTopOnMount />
							<PageHeaderTabs
					// breadcrumbs="Staff Training Topics"
					title="Staff Training Topic Library"
					// addTabTitle="Add training topic"
					// listTabTitle="View library of training topics"
					addTabPath={fromHealthSafetyForm.ADD_STAFF_TRAINING_TOPIC_ROUTE}
					hideListTabButton={true}
					backActionPath={fromHealthSafetyForm.INFO_ROUTE}
				
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}>
					{/* START: Search */}
					{showFilters && (
						<div className={classes.gridContainer}>
							<Grid container spacing={2} className={classes.filterGrid}>
								<Grid item xs={12} md={6}>
									<TextField
										className={classes.search}
										id="input-with-icon-textfield"
										variant="outlined"
										label="Search"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon />
												</InputAdornment>
											),
											endAdornment: localSearchFilter && (
												<InputAdornment position="end">
													<IconButton
														aria-label="clear search"
														onClick={() => {
															setLocalSearchFilter("");
															onSearchChanged("");
														}}
														edge="end"
														size="small"
													>
														<ClearIcon />
													</IconButton>
												</InputAdornment>
											)
										}}
										value={localSearchFilter}
										onChange={(val) => {
											const { value } = val.target;
											setLocalSearchFilter(value);
											// Real-time search with debounce effect will handle the API call
										}}
										style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
										placeholder={"e.g. Training Topic Name, Valid Duration"}
									/>
								</Grid>
							</Grid>
						</div>
					)}
					{/* END: Search */}
				</PageHeaderTabs>
			<PageContent pad>
				<PaginationHeader
					hidePagination
					pagination={staffTrainingTopics}
				/>

				{!empty &&
					staffTrainingTopicList.map((node, index) => (
						<StaffTrainingTopicCard
							key={index}
							trainingTopic={node}
							role={role}
							onView={onView(node.id)}
							onEdit={onEdit(node.id)}
							onDelete={onDelete}
						/>
					))}

				{empty && (
					<Typography variant="body1">
						There are currently no staff training topics listed.
					</Typography>
				)}

				<Pagination
					hidePagination
					onGetList={onGetStaffTrainingTopicList}
					pagination={staffTrainingTopics}
					orderBy="createdAt"
					ascending={false}
					searchKey={searchFilter}
				/>
			</PageContent>
			{viewId && (
				<EditStaffTrainingTopicForm
					readOnly
					id={viewId}
					onClose={onCancelEdit}
				/>
			)}

			{editId && (
				<EditStaffTrainingTopicForm
					id={editId}
					onClose={onCancelEdit}
				/>
			)}
		</Page>
	);
};

StaffTrainingTopicList.propTypes = {
	role: PropTypes.string.isRequired,
	searchFilter: PropTypes.string,
	staffTrainingTopics: PropTypes.object.isRequired,
	onDelete: PropTypes.func.isRequired,
	onSearchChanged: PropTypes.func.isRequired,
	onGetStaffTrainingTopicList: PropTypes.func.isRequired,
};

export default StaffTrainingTopicList;
